import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface PublicSeoPage {
  title: string;
  description: string;
  path: string;
  image?: string;
}

@Injectable({ providedIn: 'root' })
export class PublicSeoService {
  private readonly origin = 'https://flagger-ai.app';
  private readonly defaultImage = '/assets/flaggerFLarger.png';

  constructor(
    private readonly title: Title,
    private readonly meta: Meta,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  setPublicPage(page: PublicSeoPage): void {
    const canonicalUrl = this.absoluteUrl(page.path);
    const imageUrl = this.absoluteUrl(page.image ?? this.defaultImage);

    const pageTitle = page.title.replace(/\bFlaggerAI\b(?! Central)/g, 'FlaggerAI Central');
    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: page.description });
    this.meta.updateTag({ name: 'robots', content: 'index,follow' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: page.description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: page.description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    this.setCanonical(canonicalUrl);
  }

  setNoIndex(): void {
    this.title.setTitle('FlaggerAI Central');
    this.meta.updateTag({ name: 'robots', content: 'noindex,nofollow' });
    this.document.head.querySelector('link[rel="canonical"]')?.remove();

    for (const selector of [
      "property='og:type'",
      "property='og:title'",
      "property='og:description'",
      "property='og:url'",
      "property='og:image'",
      "name='twitter:card'",
      "name='twitter:title'",
      "name='twitter:description'",
      "name='twitter:image'",
    ]) {
      this.meta.removeTag(selector);
    }
  }

  private setCanonical(url: string): void {
    let canonical = this.document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );

    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.rel = 'canonical';
      this.document.head.appendChild(canonical);
    }

    canonical.href = url;
  }

  private absoluteUrl(value: string): string {
    if (/^https?:\/\//i.test(value)) return value;
    const path = value.startsWith('/') ? value : `/${value}`;
    return path === '/' ? `${this.origin}/` : `${this.origin}${path.replace(/\/$/, '')}`;
  }
}
